import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FaPlus, FaEdit, FaTrash, FaCheck, FaTimes, FaSave } from 'react-icons/fa';
import { useAuth } from '../AuthContext';
import ConfirmModal from '../../common/ConfirmModal';
import { rootUrl } from '../../../config/config';

const API = `${rootUrl}/drivers/statuses`;

const emptyForm = { code: '', name: '', active: true, is_default: false, allows_assignment: true, sort_order: 0 };

const Flag = ({ value }) => value
    ? <FaCheck className="text-green-500 mx-auto" />
    : <FaTimes className="text-gray-300 mx-auto" />;

export default function DriverStatusAdmin() {
    const { token, user } = useAuth();
    const authToken = token || localStorage.getItem('authToken');
    const headers = { Authorization: `Bearer ${authToken}` };

    const [statuses, setStatuses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [editError, setEditError] = useState('');
    const [saving, setSaving] = useState(false);
    const [showAdd, setShowAdd] = useState(false);
    const [addForm, setAddForm] = useState(emptyForm);
    const [addError, setAddError] = useState('');
    const [adding, setAdding] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteError, setDeleteError] = useState('');
    const [deleting, setDeleting] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await axios.get(API, { headers });
            setStatuses(res.data.data || []);
        } catch {
            setError('Failed to load driver statuses');
        } finally {
            setLoading(false);
        }
    }, [authToken]); // eslint-disable-line

    useEffect(() => { load(); }, [load]);

    const startEdit = (s) => {
        setEditingId(s.id);
        setEditForm({ code: s.code, name: s.name, active: s.active, is_default: s.is_default, allows_assignment: s.allows_assignment, sort_order: s.sort_order });
        setEditError('');
    };

    const cancelEdit = () => { setEditingId(null); setEditForm({}); setEditError(''); };

    const saveEdit = async () => {
        if (!editForm.code.trim() || !editForm.name.trim()) { setEditError('Code and Name are required'); return; }
        setSaving(true);
        setEditError('');
        try {
            await axios.put(`${API}/${editingId}`, { ...editForm, code: editForm.code.toUpperCase(), edit_by: user?.username }, { headers });
            cancelEdit();
            load();
        } catch (err) {
            setEditError(err.response?.data?.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const handleAdd = async () => {
        if (!addForm.code.trim() || !addForm.name.trim()) { setAddError('Code and Name are required'); return; }
        setAdding(true);
        setAddError('');
        try {
            await axios.post(API, { ...addForm, code: addForm.code.toUpperCase(), input_by: user?.username }, { headers });
            setShowAdd(false);
            setAddForm(emptyForm);
            load();
        } catch (err) {
            setAddError(err.response?.data?.message || 'Add failed');
        } finally {
            setAdding(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        setDeleteError('');
        try {
            await axios.delete(`${API}/${deleteTarget.id}`, { headers });
            setDeleteTarget(null);
            load();
        } catch (err) {
            setDeleteError(err.response?.data?.message || 'Delete failed');
        } finally {
            setDeleting(false);
        }
    };

    const inputCls = 'border border-gray-300 rounded px-2 py-1 text-sm w-full focus:outline-none focus:ring-1 focus:ring-orange-400';

    const EditableRow = ({ s }) => (
        <tr className="bg-orange-50">
            <td className="px-3 py-2"><input type="number" value={editForm.sort_order} onChange={e => setEditForm(f => ({ ...f, sort_order: Number(e.target.value) }))} className={`${inputCls} w-16`} /></td>
            <td className="px-3 py-2"><input value={editForm.code} onChange={e => setEditForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} className={inputCls} maxLength={25} /></td>
            <td className="px-3 py-2"><input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} className={inputCls} /></td>
            <td className="px-3 py-2 text-center"><input type="checkbox" checked={editForm.active} onChange={e => setEditForm(f => ({ ...f, active: e.target.checked }))} className="w-4 h-4 accent-orange-500" /></td>
            <td className="px-3 py-2 text-center"><input type="checkbox" checked={editForm.is_default} onChange={e => setEditForm(f => ({ ...f, is_default: e.target.checked }))} className="w-4 h-4 accent-orange-500" /></td>
            <td className="px-3 py-2 text-center"><input type="checkbox" checked={editForm.allows_assignment} onChange={e => setEditForm(f => ({ ...f, allows_assignment: e.target.checked }))} className="w-4 h-4 accent-orange-500" /></td>
            <td className="px-3 py-2">
                {editError && <p className="text-red-500 text-xs mb-1">{editError}</p>}
                <div className="flex gap-2">
                    <button onClick={saveEdit} disabled={saving} className="flex items-center gap-1 px-3 py-1 bg-orange-500 text-white rounded text-xs hover:bg-orange-600 disabled:opacity-50"><FaSave /> {saving ? 'Saving…' : 'Save'}</button>
                    <button onClick={cancelEdit} className="flex items-center gap-1 px-3 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300"><FaTimes /> Cancel</button>
                </div>
            </td>
        </tr>
    );

    return (
        <div className="p-6 max-w-5xl mx-auto">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-[#2a2e30]">Driver Statuses</h1>
                    <p className="text-sm text-gray-500 mt-1">Manage available statuses for drivers</p>
                </div>
                <button onClick={() => { setShowAdd(true); setAddError(''); setAddForm(emptyForm); }} className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600">
                    <FaPlus /> Add Status
                </button>
            </div>

            {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}

            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                            <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Order</th>
                            <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Code</th>
                            <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                            <th className="px-3 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Active</th>
                            <th className="px-3 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Default</th>
                            <th className="px-3 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Can Assign</th>
                            <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {loading && (
                            <tr><td colSpan={7} className="px-3 py-8 text-center text-gray-400">Loading…</td></tr>
                        )}
                        {!loading && statuses.length === 0 && (
                            <tr><td colSpan={7} className="px-3 py-8 text-center text-gray-400">No statuses found</td></tr>
                        )}
                        {!loading && statuses.map(s => editingId === s.id
                            ? <EditableRow key={s.id} s={s} />
                            : (
                                <tr key={s.id} className={`hover:bg-gray-50 ${!s.active ? 'opacity-50' : ''}`}>
                                    <td className="px-3 py-3 text-gray-500">{s.sort_order}</td>
                                    <td className="px-3 py-3 font-mono font-semibold text-[#2a2e30]">{s.code}</td>
                                    <td className="px-3 py-3 text-[#2a2e30]">{s.name}</td>
                                    <td className="px-3 py-3 text-center"><Flag value={s.active} /></td>
                                    <td className="px-3 py-3 text-center"><Flag value={s.is_default} /></td>
                                    <td className="px-3 py-3 text-center"><Flag value={s.allows_assignment} /></td>
                                    <td className="px-3 py-3">
                                        <div className="flex gap-2">
                                            <button onClick={() => startEdit(s)} className="p-1.5 text-orange-500 hover:text-orange-700" title="Edit"><FaEdit /></button>
                                            <button onClick={() => { setDeleteTarget(s); setDeleteError(''); }} className="p-1.5 text-red-400 hover:text-red-600" title="Delete"><FaTrash /></button>
                                        </div>
                                    </td>
                                </tr>
                            )
                        )}
                        {showAdd && (
                            <tr className="bg-green-50">
                                <td className="px-3 py-2"><input type="number" value={addForm.sort_order} onChange={e => setAddForm(f => ({ ...f, sort_order: Number(e.target.value) }))} className={`${inputCls} w-16`} /></td>
                                <td className="px-3 py-2"><input value={addForm.code} onChange={e => setAddForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} className={inputCls} maxLength={25} placeholder="CODE" /></td>
                                <td className="px-3 py-2"><input value={addForm.name} onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))} className={inputCls} placeholder="Status name" /></td>
                                <td className="px-3 py-2 text-center"><input type="checkbox" checked={addForm.active} onChange={e => setAddForm(f => ({ ...f, active: e.target.checked }))} className="w-4 h-4 accent-orange-500" /></td>
                                <td className="px-3 py-2 text-center"><input type="checkbox" checked={addForm.is_default} onChange={e => setAddForm(f => ({ ...f, is_default: e.target.checked }))} className="w-4 h-4 accent-orange-500" /></td>
                                <td className="px-3 py-2 text-center"><input type="checkbox" checked={addForm.allows_assignment} onChange={e => setAddForm(f => ({ ...f, allows_assignment: e.target.checked }))} className="w-4 h-4 accent-orange-500" /></td>
                                <td className="px-3 py-2">
                                    {addError && <p className="text-red-500 text-xs mb-1">{addError}</p>}
                                    <div className="flex gap-2">
                                        <button onClick={handleAdd} disabled={adding} className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50"><FaSave /> {adding ? 'Adding…' : 'Add'}</button>
                                        <button onClick={() => setShowAdd(false)} className="flex items-center gap-1 px-3 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300"><FaTimes /> Cancel</button>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <ConfirmModal
                isOpen={Boolean(deleteTarget)}
                title="Delete Driver Status"
                description={`Delete "${deleteTarget?.name}"? This cannot be undone.`}
                confirmText="Delete"
                cancelText="Cancel"
                variant="danger"
                loading={deleting}
                error={deleteError}
                onCancel={() => { setDeleteTarget(null); setDeleteError(''); }}
                onConfirm={handleDelete}
            />
        </div>
    );
}
